/**
 * coalesced-logger
 *
 * Helper de logging con throttle por ventana (timer-free) para hot paths
 * (audio por chunk, mensajes WebSocket por frame) que de otro modo generan
 * decenas de miles de líneas por sesión y ahogan los warn/error reales.
 *
 * Contrato:
 *  - La PRIMERA ocurrencia de una `key` se imprime al instante (fidelidad total).
 *  - Las repeticiones dentro de la ventana se acumulan (se cuenta y se guarda el
 *    último payload), SIN imprimir.
 *  - La primera llamada que llega ya pasada la ventana emite UNA línea que
 *    representa lo acumulado: si fueron >1 ocurrencias, `<primer arg> (×N) <último payload>`;
 *    si fue solo 1, el payload tal cual (sin anotación).
 *  - El último lote pendiente se vacía en el teardown vía `clearCoalesced()`.
 *
 * Es deliberadamente SIN setTimeout: así no ensucia las zonas `fakeAsync` de los
 * tests ("timer(s) still in the queue"), no deja timers de fondo durante un examen
 * y es SSR-safe. No elimina información: ves el primer evento y el más reciente,
 * más el conteo.
 *
 * Flags runtime (espejo de fraud-logger `__FRAUD_LOG_OFF__`):
 *   (window as any).__COALESCE_LOG_OFF__ = true;      // silencio total
 *   (window as any).__COALESCE_LOG_VERBOSE__ = true;  // bypass: imprime todo (deep-debug)
 *
 * Las keys deben ser de BAJA cardinalidad (enums/roles), nunca payload crudo.
 */

export type CoalesceLevel = 'log' | 'info' | 'warn' | 'error';

export interface CoalesceOptions {
  /** Ventana de throttle en ms. Default 1200. */
  windowMs?: number;
  /** Nivel de consola. Default 'log'. */
  level?: CoalesceLevel;
}

interface Bucket {
  pending: number; // ocurrencias desde la última emisión, aún no representadas
  windowStart: number; // ts (ms) de la última emisión
  latestArgs: unknown[]; // último payload visto → se preserva en el resumen
  level: CoalesceLevel;
}

const DEFAULT_WINDOW_MS = 1200;

const buckets = new Map<string, Bucket>();

function isOff(): boolean {
  if (typeof window === 'undefined') return true; // SSR: nunca loguea
  return (window as any).__COALESCE_LOG_OFF__ === true;
}

function isVerbose(): boolean {
  if (typeof window === 'undefined') return false;
  return (window as any).__COALESCE_LOG_VERBOSE__ === true;
}

function emit(level: CoalesceLevel, args: unknown[]): void {
  // eslint-disable-next-line no-console
  (console[level] as (...a: unknown[]) => void)(...args);
}

function summary(args: unknown[], total: number): unknown[] {
  const [head, ...tail] = args;
  const headStr = typeof head === 'string' ? head : String(head);
  return [`${headStr} (×${total})`, ...tail];
}

/**
 * Variante con opciones (nivel / ventana). La opción NO es arg variádico de
 * `coalesceLog` para evitar ambigüedad con `...args`.
 */
export function coalesceLogWith(key: string, opts: CoalesceOptions, ...args: unknown[]): void {
  if (isOff()) return;
  const level = opts.level ?? 'log';

  if (isVerbose()) {
    emit(level, args); // bypass: imprime todo
    return;
  }

  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const now = Date.now();
  const bucket = buckets.get(key);

  if (!bucket) {
    // LEADING EDGE: imprime ya y abre la ventana.
    emit(level, args);
    buckets.set(key, { pending: 0, windowStart: now, latestArgs: args, level });
    return;
  }

  if (now - bucket.windowStart >= windowMs) {
    // Pasó la ventana: emite una línea que representa lo acumulado + esta llamada.
    bucket.pending += 1;
    if (bucket.pending > 1) {
      emit(level, summary(args, bucket.pending));
    } else {
      emit(level, args);
    }
    bucket.pending = 0;
    bucket.windowStart = now;
    bucket.latestArgs = args;
    bucket.level = level;
    return;
  }

  // DENTRO DE VENTANA: acumula, cuenta y recuerda el último payload.
  bucket.pending += 1;
  bucket.latestArgs = args;
  bucket.level = level;
}

/** Llamada principal para hot paths (nivel 'log', ventana default). */
export function coalesceLog(key: string, ...args: unknown[]): void {
  coalesceLogWith(key, {}, ...args);
}

/** Emite el lote pendiente de una key (si hay) y cierra su bucket. */
export function flushCoalesced(key: string): void {
  const bucket = buckets.get(key);
  if (!bucket) return;
  buckets.delete(key);
  if (bucket.pending > 1) {
    emit(bucket.level, summary(bucket.latestArgs, bucket.pending));
  } else if (bucket.pending === 1) {
    emit(bucket.level, bucket.latestArgs);
  }
}

/** Flush + clear de TODAS las keys. Llamar en teardown para vaciar lo pendiente. */
export function clearCoalesced(): void {
  for (const key of Array.from(buckets.keys())) {
    flushCoalesced(key);
  }
}
